import React, { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';

// Detects PWA install opportunities:
//  - Chrome/Edge/Android: beforeinstallprompt event → show "Installa app" button
//  - iOS Safari: shows hint card explaining "Aggiungi alla schermata Home" via share sheet
//  - Already installed (display-mode: standalone): hidden
const DISMISS_KEY = 'metronomo:install-dismissed:v1';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());
  const [dismissed, setDismissed] = useState(() => {
    try {
      const t = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      // re-show after 7 days
      return Date.now() - t < 7 * 24 * 60 * 60 * 1000;
    } catch { return false; }
  });

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    // iOS detection: show hint if iOS Safari and not installed
    if (isIOS() && !isStandalone()) {
      setShowIosHint(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {/* ignore */}
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {/* ignore */}
    setDismissed(true);
  };

  if (installed || dismissed) return null;

  // Chrome/Edge/Android → install button
  if (deferredPrompt) {
    return (
      <div
        data-testid="install-prompt"
        style={{ bottom: "calc(var(--donation-h) + env(safe-area-inset-bottom) + 5rem)" }}
        className="fixed right-4 z-50 max-w-sm bg-[#111112] border border-pink-400/40 rounded-xl shadow-[0_0_30px_rgba(236,72,153,0.25)] p-4 flex items-start gap-3"
      >
        <div className="bg-pink-400/20 p-2 rounded-lg shrink-0">
          <Download className="w-5 h-5 text-pink-300" />
        </div>
        <div className="flex-1">
          <div className="font-bebas text-lg tracking-wide text-white leading-tight">
            Installa Metronomo
          </div>
          <p className="text-xs text-white/60 mt-0.5 leading-snug">
            Aggiungilo come app: si apre senza browser, funziona offline.
          </p>
          <div className="flex gap-2 mt-2.5">
            <button
              data-testid="install-now-btn"
              onClick={handleInstall}
              className="bg-pink-400 hover:bg-pink-300 text-black text-xs font-medium px-3 py-1.5 rounded uppercase tracking-widest active:scale-95 transition-all"
            >
              Installa
            </button>
            <button
              data-testid="install-dismiss-btn"
              onClick={dismiss}
              className="text-xs text-white/50 hover:text-white px-3 py-1.5 rounded uppercase tracking-widest"
            >
              Più tardi
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-white/40 hover:text-white p-1 rounded transition-all"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // iOS → instructional hint
  if (showIosHint) {
    return (
      <div
        data-testid="ios-install-hint"
        style={{ bottom: "calc(var(--donation-h) + env(safe-area-inset-bottom) + 5rem)" }}
        className="fixed right-4 left-4 sm:left-auto sm:right-4 z-50 max-w-sm bg-[#111112] border border-pink-400/40 rounded-xl shadow-[0_0_30px_rgba(236,72,153,0.25)] p-4 flex items-start gap-3"
      >
        <div className="bg-pink-400/20 p-2 rounded-lg shrink-0">
          <Share className="w-5 h-5 text-pink-300" />
        </div>
        <div className="flex-1">
          <div className="font-bebas text-lg tracking-wide text-white leading-tight">
            Installa su iPhone
          </div>
          <p className="text-xs text-white/60 mt-0.5 leading-snug">
            Tocca <Share className="inline w-3 h-3 mx-0.5" /> in Safari, poi
            <span className="text-pink-300"> "Aggiungi alla schermata Home"</span>.
          </p>
        </div>
        <button
          onClick={dismiss}
          className="text-white/40 hover:text-white p-1 rounded transition-all"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return null;
}
